import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { 
  LayoutDashboard, 
  Table2, 
  BarChart3, 
  MapPin, 
  TrendingUp,
  Upload,
  LogOut,
  Menu,
  X,
  FileText
} from "lucide-react";

// Import views
import DashboardOverview from "../components/DashboardOverview";
import PreventiviTable from "../components/PreventiviTable";
import AnalyticsTC from "../components/AnalyticsTC";
import AnalyticsSede from "../components/AnalyticsSede";
import AnalyticsTrend from "../components/AnalyticsTrend";
import UploadModal from "../components/UploadModal";

const DashboardPage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  
  // Data state
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [preventivi, setPreventivi] = useState([]);
  const [filters, setFilters] = useState({ sedi: [], tcs: [] });
  const [loading, setLoading] = useState(true);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
      if (response.data.length > 0 && !selectedSession) {
        setSelectedSession(response.data[0].session_id);
      }
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  }, [token, selectedSession]);

  const fetchData = useCallback(async () => {
    if (!selectedSession) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const [kpiRes, prevRes, filterRes] = await Promise.all([
        axios.get(`${API}/kpi?session_id=${selectedSession}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/preventivi?session_id=${selectedSession}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/filters?session_id=${selectedSession}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setKpi(kpiRes.data);
      setPreventivi(prevRes.data);
      setFilters(filterRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [selectedSession, token]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUploadSuccess = (sessionId) => {
    setSelectedSession(sessionId);
    fetchSessions();
    setUploadModalOpen(false);
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Sessione eliminata");
      if (selectedSession === sessionId) {
        setSelectedSession(null);
        setKpi(null);
        setPreventivi([]);
      }
      fetchSessions();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/preventivi", icon: Table2, label: "Preventivi" },
    { path: "/analytics/tc", icon: BarChart3, label: "Performance TC" },
    { path: "/analytics/sede", icon: MapPin, label: "Per Sede" },
    { path: "/analytics/trend", icon: TrendingUp, label: "Trend" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dashboard-page">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 p-6 border-b border-slate-800">
            <img 
              src="https://acrobatica.es/app/themes/edilizia-acrobatica-theme/dist/images/pin-acrobatica-blu_c6fc7f4f.svg" 
              alt="Logo" 
              className="h-10 w-10"
            />
            <div className="flex-1">
              <h1 className="font-heading text-sm font-bold text-white uppercase tracking-wider">
                Performance
              </h1>
              <p className="text-xs text-slate-400">Dashboard</p>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive 
                      ? "bg-orange-500 text-white" 
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-slate-600 hover:text-slate-900"
              >
                <Menu className="h-6 w-6" />
              </button>
              
              {/* Session Selector */}
              {sessions.length > 0 && (
                <select
                  value={selectedSession || ""}
                  onChange={(e) => setSelectedSession(e.target.value)}
                  className="text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="session-selector"
                >
                  {sessions.map((s) => (
                    <option key={s.session_id} value={s.session_id}>
                      {new Date(s.uploaded_at).toLocaleDateString("it-IT")} - {s.count} preventivi
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button
                onClick={() => setUploadModalOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="upload-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Carica Excel
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
          ) : !selectedSession ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FileText className="h-16 w-16 text-slate-300 mb-4" />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                Nessun dato disponibile
              </h2>
              <p className="text-slate-500 mb-6">
                Carica un file Excel per iniziare l'analisi
              </p>
              <Button
                onClick={() => setUploadModalOpen(true)}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="upload-empty-btn"
              >
                <Upload className="h-4 w-4 mr-2" />
                Carica il tuo primo file
              </Button>
            </div>
          ) : (
            <Routes>
              <Route 
                path="/" 
                element={
                  <DashboardOverview 
                    kpi={kpi} 
                    preventivi={preventivi}
                    filters={filters}
                  />
                } 
              />
              <Route 
                path="/preventivi" 
                element={
                  <PreventiviTable 
                    preventivi={preventivi}
                    filters={filters}
                    sessionId={selectedSession}
                    token={token}
                  />
                } 
              />
              <Route 
                path="/analytics/tc" 
                element={<AnalyticsTC kpi={kpi} preventivi={preventivi} />} 
              />
              <Route 
                path="/analytics/sede" 
                element={<AnalyticsSede kpi={kpi} preventivi={preventivi} />} 
              />
              <Route 
                path="/analytics/trend" 
                element={<AnalyticsTrend preventivi={preventivi} sessionId={selectedSession} token={token} />} 
              />
            </Routes>
          )}
        </main>
      </div>

      {/* Upload Modal */}
      <UploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onSuccess={handleUploadSuccess}
        token={token}
      />

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardPage;
